import { useCallback, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { stageKey } from "./closeTemplateTasksDrawer.constants";
import { CloseTemplateTaskRow } from "./closeTemplateTasksViewConfig";
import { LocalTask } from "./closeTemplateTasksDrawer.types";

interface UseCloseTemplateTaskSelectionParams {
    localTasks: LocalTask[];
    tableRows: CloseTemplateTaskRow[];
}

export function useCloseTemplateTaskSelection({
    localTasks,
    tableRows,
}: UseCloseTemplateTaskSelectionParams) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const clearSelection = useCallback(() => {
        setRowSelection({});
    }, []);

    const canSelectRow = useCallback(
        (row: CloseTemplateTaskRow) =>
            !row.isGroupRow || !!row.children?.length,
        [],
    );

    // Derive selected local IDs as a Set directly (avoids redundant array allocation)
    const selectedLocalIdsSet = useMemo(() => {
        const set = new Set<string>();
        for (const [localId, selected] of Object.entries(rowSelection)) {
            if (selected) {
                // Exclude deleted tasks from selection
                const task = localTasks.find((t) => t.localId === localId);
                if (task && !task.isDeleted) {
                    set.add(localId);
                }
            }
        }
        return set;
    }, [rowSelection, localTasks]);

    const selectedLocalIds = useMemo(
        () => Array.from(selectedLocalIdsSet),
        [selectedLocalIdsSet],
    );

    const handleBulkSelectStage = useCallback(
        (closeStageId: string | null) => {
            // Use taskIdsByStageKey derived from tableRows (search-filtered)
            // so that "Select all" / "Deselect all" only affects visible tasks
            const key = stageKey(closeStageId);
            const group = tableRows.find((row) => row.id === key);
            const stageTaskIds = (group?.children ?? []).map((c) => c.id);

            if (stageTaskIds.length === 0) {
                return;
            }

            setRowSelection((prev) => {
                const allSelected = stageTaskIds.every((id) => prev[id]);
                const next = { ...prev };
                stageTaskIds.forEach((id) => {
                    if (allSelected) {
                        delete next[id];
                    } else {
                        next[id] = true;
                    }
                });
                return next;
            });
        },
        [tableRows],
    );

    // Map stage key -> active task localIds (for per-section selection UI in cells)
    const taskIdsByStageKey = useMemo(() => {
        const map = new Map<string, string[]>();
        tableRows.forEach((group) => {
            map.set(
                group.id,
                (group.children ?? []).map((c) => c.id),
            );
        });
        return map;
    }, [tableRows]);

    return {
        rowSelection,
        setRowSelection,
        clearSelection,
        canSelectRow,
        selectedLocalIds,
        selectedLocalIdsSet,
        handleBulkSelectStage,
        taskIdsByStageKey,
    };
}
